import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import type { SalaryNegotiationResponse, ScenarioInfo } from "../../lib/api";
import { useApiClient } from "../../lib/api";
import { DollarSign, Play } from 'lucide-react';

interface SalaryNegotiationListProps {
  jobId?: string;
  onEmptyStateCreate?: () => void;
  hideNewButton?: boolean;
  showTitle?: boolean;
}

const SalaryNegotiationList: React.FC<SalaryNegotiationListProps> = ({ 
  jobId, 
  onEmptyStateCreate, 
  hideNewButton,
  showTitle = false
}) => {
  const navigate = useNavigate();
  const { api } = useApiClient();
  const [negotiations, setNegotiations] = useState<SalaryNegotiationResponse[]>([]);
  const [scenarios, setScenarios] = useState<ScenarioInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [negotiationsData, scenariosData] = await Promise.all([
        api.getNegotiations(jobId),
        api.getScenarios(),
      ]);
      setNegotiations(negotiationsData);
      setScenarios(scenariosData);
    } catch (error) {
      setNegotiations([]);
    } finally {
      setLoading(false);
    }
  };

  const getScenarioName = (scenarioType: string) => {
    const scenario = scenarios.find(s => s.id === scenarioType);
    return scenario?.label || scenarioType;
  };

  const getScoreLetter = (score: number | null | undefined) => {
    if (score == null) return '-';
    if (score > 3.9) return "S";
    if (score > 3.4) return "A";
    if (score > 2.9) return "B";
    if (score > 1.9) return "C";
    if (score > 1.4) return "D";
    return "F";
  };

  const getStatusBadge = (negotiation: SalaryNegotiationResponse) => {
    const status = negotiation.status;
    switch (status) {
      case 'GRADING':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Grading</Badge>;
      case 'ACTIVE':
      case 'TERMINATED':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">In Progress</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleResumeNegotiation = (negotiation: SalaryNegotiationResponse) => {
    if (negotiation.job_id) {
      navigate(`/jobs/${negotiation.job_id}/negotiation/${negotiation.id}`);
    } else {
      navigate(`/salary-negotiations`);
    }
  };

  // Render
  if (loading) {
    return (
      <div className="py-8 flex flex-col items-center">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mb-2" />
        <div className="text-gray-500">Loading negotiations...</div>
      </div>
    );
  }

  // Separate in-progress and completed
  const inProgress = negotiations.filter(n => n.status !== 'COMPLETE');
  const completed = negotiations.filter(n => n.status === 'COMPLETE');

  // If both are empty
  if (inProgress.length === 0 && completed.length === 0) {
    if (showTitle) {
      return null; // Hide completely if showTitle is true and no negotiations
    }
    return (
      <div className="py-8 flex flex-col items-center">
        <div className="text-gray-500 mb-2">No negotiations found{jobId ? ' for this job' : ''}.</div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {showTitle && (inProgress.length > 0 || completed.length > 0) && (
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Your Negotiations</h4>
      )}
      {/* In Progress Negotiations */}
      {inProgress.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">In Progress</h3>
          <div className="space-y-0 sm:space-y-3">
            {inProgress.map((negotiation) => (
              <Card
                key={negotiation.id}
                className="hover:shadow-lg transition-all duration-200 cursor-pointer border-l-4 border-l-green-400 hover:border-l-green-600 rounded-none sm:rounded-lg border-r-0 sm:border-r border-t-0 sm:border-t border-b sm:border-b shadow-none sm:shadow-sm"
                onClick={() => handleResumeNegotiation(negotiation)}
              >
                <CardHeader className="pb-3 px-4 sm:px-6 pt-4 sm:pt-6">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base sm:text-base flex items-start gap-2">
                          <DollarSign className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="break-words">{getScenarioName(negotiation.scenario_type)}</span>
                        </CardTitle>
                        <div className="text-xs text-gray-600 mt-1 flex flex-wrap items-center gap-1.5">
                          <span>{formatDate(negotiation.created_at)}</span>
                          {negotiation.messages && negotiation.messages.length > 0 && (
                            <>
                              <span className="text-gray-400">•</span>
                              <span>{negotiation.messages.length} messages</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 self-start sm:self-center">
                        {getStatusBadge(negotiation)}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="bg-green-500 hover:bg-green-600 text-white rounded-full h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResumeNegotiation(negotiation);
                          }}
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Completed Negotiations */}
      {completed.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Completed</h3>
          <div className="space-y-0 sm:space-y-3">
            {completed.map((negotiation) => {
              const gradeLetter = getScoreLetter(negotiation.overall_score);
              
              // Determine right side color based on grade
              let rightSideColor = 'bg-green-500';
              if (gradeLetter === 'S') rightSideColor = 'bg-gradient-to-b from-amber-400 to-amber-500';
              else if (gradeLetter === 'A' || gradeLetter === 'B') rightSideColor = 'bg-green-500';
              else if (gradeLetter === 'C') rightSideColor = 'bg-yellow-500';
              else if (gradeLetter === 'D' || gradeLetter === 'F') rightSideColor = 'bg-red-500';
              
              return (
                <Card
                  key={negotiation.id}
                  className="hover:shadow-lg transition-all duration-200 cursor-pointer relative overflow-hidden rounded-none sm:rounded-lg border-r-0 sm:border-r border-t-0 sm:border-t border-b sm:border-b shadow-none sm:shadow-sm"
                  onClick={() => handleResumeNegotiation(negotiation)}
                >
                  {/* Colored right side */}
                  <div className={`absolute right-0 top-0 bottom-0 w-14 sm:w-20 ${rightSideColor} flex items-center justify-center`}>
                    <span className="text-2xl sm:text-4xl font-bold text-white drop-shadow-lg">
                      {gradeLetter}
                    </span>
                  </div>
                  <CardHeader className="pr-16 sm:pr-28 px-4 sm:px-6 pt-4 sm:pt-6">
                    <div className="flex flex-col gap-2">
                      <CardTitle className="text-base flex items-start gap-2">
                        <DollarSign className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="break-words">{getScenarioName(negotiation.scenario_type)}</span>
                      </CardTitle>
                      <div className="text-xs text-gray-600 flex flex-wrap items-center gap-1.5">
                        <span>{formatDate(negotiation.created_at)}</span>
                        {negotiation.messages && negotiation.messages.length > 0 && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span>{negotiation.messages.length} messages</span>
                          </>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryNegotiationList;

